'use client'

import React, { useCallback } from 'react'

import * as DialogPrimitive from '@radix-ui/react-dialog'
import { HiXMark } from 'react-icons/hi2'
import { twMerge } from 'tailwind-merge'

import IconButton from '@/components/common/IconButton'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    // Fades through its own keyframes rather than `animate-in`/`animate-out`,
    // which would interpolate a transform. This overlay is the scroll container
    // for dialogs taller than the window, and a transform on it — Chrome counts
    // an animated one whether or not it moves anything — makes it the anchor for
    // the fixed backdrop below, which then rides the scroll instead of the
    // viewport. Closing a dialog somebody had scrolled left the bottom of the
    // page undimmed for the length of the animation.
    className={twMerge(
      'fixed inset-0 z-50 grid place-items-center overflow-y-auto px-4 py-0 data-[state=open]:animate-dialog-overlay-in data-[state=closed]:animate-dialog-overlay-out md:px-6 md:py-12',
      className,
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

type DialogContentProps = React.ComponentPropsWithoutRef<
  typeof DialogPrimitive.Content
> & {
  closeButtonClassName?: string
  overlayClassName?: string
  backdropClassName?: string
}

type PointerDownOutsideEvent = CustomEvent<{
  originalEvent: PointerEvent
}>

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      className,
      children,
      closeButtonClassName,
      overlayClassName,
      backdropClassName,
      onPointerDownOutside,
      ...props
    },
    ref,
  ) => {
    const handlePointerDownOutside = useCallback(
      (event: PointerDownOutsideEvent) => {
        // Check if the click target is within a toast element
        // Sonner toasts have [data-sonner-toast] attribute or are within [data-sonner-toaster]
        // Radix UI's onPointerDownOutside event may have the target in event.target or event.detail.originalEvent.target
        const originalEvent = event.detail?.originalEvent
        const target = (originalEvent?.target ||
          event.target) as HTMLElement | null

        if (target) {
          // Check if the target or any of its parents is a toast element
          // Sonner uses [data-sonner-toast] for individual toasts and [data-sonner-toaster] for the container
          // Mention suggestion lists are also appended to the body (see extensions/Mention)
          const isBodyPortalElement =
            target.closest('[data-sonner-toast]') ||
            target.closest('[data-sonner-toaster]') ||
            target.closest('[data-mention-list]')

          if (isBodyPortalElement) {
            // Prevent dialog from closing when clicking on toast or mention list
            event.preventDefault()
            return
          }
        }

        // Call the custom handler if provided
        if (onPointerDownOutside) {
          onPointerDownOutside(event)
        }
      },
      [onPointerDownOutside],
    )

    return (
      <DialogPortal>
        <DialogOverlay className={twMerge(overlayClassName)}>
          <div
            aria-hidden={true}
            className={twMerge(
              'pointer-events-none fixed inset-0 bg-black/20 dark:bg-black/40',
              backdropClassName,
            )}
          />
          <DialogPrimitive.Content
            ref={ref}
            // `duration-200` times the open and close keyframes, but
            // transition-property defaults to `all`, so it also animates
            // `focus:outline-none` — whose outline is transparent rather than
            // absent. The first click flashed a black ring fading out over
            // 200ms. Nothing here is meant to transition.
            className={twMerge(
              'relative z-10 border bg-background p-6 shadow-lg transition-none duration-200 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95 data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0 sm:rounded-lg',
              'border border-primary-200 dark:border-primary-800 bg-white dark:bg-primary-900',
              className,
            )}
            onPointerDownOutside={handlePointerDownOutside}
            {...props}
          >
            {children}
            <DialogPrimitive.Close
              type="button"
              asChild={true}
              className={twMerge(
                'absolute right-4 top-4',
                closeButtonClassName,
              )}
            >
              <IconButton>
                <HiXMark className="h-[18px] w-[18px] text-primary-900 dark:text-primary-100" />
                <span className="sr-only">Close</span>
              </IconButton>
            </DialogPrimitive.Close>
          </DialogPrimitive.Content>
        </DialogOverlay>
      </DialogPortal>
    )
  },
)
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={twMerge(
      'flex flex-col space-y-2 text-left text-primary-900 dark:text-primary-100',
      className,
    )}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={twMerge('flex justify-end gap-2', className)} {...props} />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={twMerge(
      'text-lg font-semibold leading-none tracking-tight text-primary-900 dark:text-primary-100',
      className,
    )}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={twMerge(
      'text-sm text-primary-700 dark:text-primary-300',
      className,
    )}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

const DialogWarning = ({
  children,
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={twMerge(
      '-mx-[25px] px-[25px] py-3 font-medium text-sm text-red-900 dark:text-red-100 bg-red-200 dark:bg-red-600/30 dark:border-y dark:border-red-600/40',
      className,
    )}
    {...props}
  >
    {children}
  </div>
)
DialogWarning.displayName = 'DialogWarning'

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  DialogWarning,
}
